<template>

    <Panel>

        <PanelHeader heading="Location" subheading="Where will the sale and viewing take place" />

        <PanelBody>

            <Fieldset v-if="sale" heading="Sale location">
                <FormField :field="state.ui.fields.has_sale_location" namespace="ui_" :name="'has_sale_location'" class="mb-4" />
                <AuctioneerSelectLocation
                    v-if="state.ui.fields.has_sale_location.value === true"
                    v-on:location_change="location => locationUpdated('sale', location)"
                    :auctioneer_id="sale.auctioneer_id"
                    :current_location_id="state.sale_form.fields.sale_location_id.value"
                    :current_location_other="state.sale_form.fields.sale_location_other.value"
                    :instance_id="'sale-location'"
                />
            </Fieldset>

            <Fieldset v-if="sale" heading="Viewing location">
                <FormField :field="state.ui.fields.has_viewing_location" namespace="ui_" :name="'has_viewing_location'" class="mb-4" />
                <AuctioneerSelectLocation
                    v-if="state.ui.fields.has_viewing_location.value === true"
                    v-on:location_change="location => locationUpdated('viewing', location)"
                    :auctioneer_id="sale.auctioneer_id"
                    :current_location_id="state.sale_form.fields.viewing_location_id.value"
                    :current_location_other="state.sale_form.fields.viewing_location_other.value"
                    :instance_id="'viewing-location'"
                />
            </Fieldset>

        </PanelBody>

        <PanelFooter>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => next_step()" :disabled="!next_button_active">Next</Button>
        </PanelFooter>

    </Panel>

</template>

<script>
import { useSaleStore } from '@sto/sale.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid, getPayload, getUserOrganisationType } from '@lib/helpers'
import { useRoute, useRouter } from 'vue-router'
import config from '@/config'
import LoadingIcon from '@ico/LoadingIcon.vue'
import AuctioneerSelectLocation from "@par/auctioneer/AuctioneerSelectLocation.vue"
export default {
    components: {
        LoadingIcon,
        AuctioneerSelectLocation
    },
    setup() {
        const ROUTE = useRoute()
        const ROUTER = useRouter()
        const SALE_STORE = useSaleStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const sale = computed(() => SALE_STORE.sale)

        const state = reactive({
            sale_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.sale_form.fields) {
                        if (state.sale_form.fields[f].value != state.sale_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    sale_location_id: {
                        active: true,
                        label: 'Sale location id',
                        type: 'hidden',
                        value: sale.value?.sale_location_id || null,
                        stored_value: sale.value?.sale_location_id || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    sale_location_other: {
                        active: true,
                        type: 'hidden',
                        value: sale.value?.sale_location_other
                            ? JSON.parse(sale.value?.sale_location_other)
                            : null,
                        stored_value: sale.value?.sale_location_other
                            ? JSON.parse(sale.value?.sale_location_other)
                            : null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    viewing_location_id: {
                        active: true,
                        label: 'Viewing location id',
                        type: 'hidden',
                        value: sale.value?.viewing_location_id || null,
                        stored_value: sale.value?.viewing_location_id || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    viewing_location_other: {
                        active: true,
                        type: 'hidden',
                        value: sale.value?.viewing_location_other
                            ? JSON.parse(sale.value?.viewing_location_other)
                            : null,
                        stored_value: sale.value?.viewing_location_other
                            ? JSON.parse(sale.value?.viewing_location_other)
                            : null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    }
                }
            },
            ui: {
                fields: {
                    has_sale_location: {
                        active: true,
                        label: 'Add a sale location?',
                        type: 'toggle',
                        value: sale.value?.sale_location_id || sale.value?.sale_location_other
                            ? true
                            : false,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        hide_hint: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    has_viewing_location: {
                        active: true,
                        label: 'Add a viewing location?',
                        type: 'toggle',
                        value: sale.value?.viewing_location_id || sale.value?.viewing_location_other
                            ? true
                            : false,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        hide_hint: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            }
        })

        /**
         * should the next button be available to the user?
         */
         const next_button_active = computed(() => {
            let active = true
            for (let f in state.sale_form.fields) {
                if (state.sale_form.fields[f].active && state.sale_form.fields[f].required && !state.sale_form.fields[f].value) active = false
            }
            return active
        })

        const update_sale = async () => {

            if (state.sale_form.loading) return

            state.sale_form.loading = true

            if (!isValid(state.sale_form.fields)) {
                return state.sale_form.loading = false
            }

            const payload = getPayload(state.sale_form.fields)

            try {
                const sale = await SALE_STORE.updateSale(ROUTE.params.sale_id, payload)
                SALE_STORE.activateNextStep()

            } catch (error) {
                console.error('SALE_STORE.updateSale error', error)
                SYSTEM_STORE.addError('Something went wrong', error?.message || 'There was a problem updating the sale')

            } finally {
                state.sale_form.loading = false
            }
        }

        const locationUpdated = (scope, location) => {

            switch (scope) {
                case 'sale':
                    if (location.id === 'OTHER') {
                        state.sale_form.fields.sale_location_id.value = null
                        state.sale_form.fields.sale_location_other.value = location
                    } else {
                        state.sale_form.fields.sale_location_id.value = location?.id || null
                        state.sale_form.fields.sale_location_other.value = null
                    }
                    break;
                case 'viewing':
                    state.sale_form.fields.viewing_location_id.value = location?.id || null
                    if (location.id === 'OTHER') {
                        state.sale_form.fields.viewing_location_id.value = null
                        state.sale_form.fields.viewing_location_other.value = location
                    } else {
                        state.sale_form.fields.viewing_location_id.value = location?.id || null
                        state.sale_form.fields.viewing_location_other.value = null
                    }
                    break;
            }
        }

        watch(
            () => state.ui.fields.has_sale_location.value,
            (new_value, old_value) => {
                if (!new_value) {
                    state.sale_form.fields.sale_location_id.value = null
                    state.sale_form.fields.sale_location_other.value = null
                }
            }
        )

        watch(
            () => state.ui.fields.has_viewing_location.value,
            (new_value, old_value) => {
                if (!new_value) {
                    state.sale_form.fields.viewing_location_id.value = null
                    state.sale_form.fields.viewing_location_other.value = null
                }
            }
        )

        const next_step = async () => {
            await update_sale()
        }

        onBeforeMount(() => {
            
        })

        return {
            SALE_STORE,
            ROUTER,
            sale,
            next_button_active,
            state,
            next_step,
            locationUpdated
        }
    }
}

</script>

